import { MessagesResponse as Response } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { useConversations } from 'chat/conversations/queries/list'
import { useChatWebsocketContext } from 'chat/websocket/context'
import { useUser } from 'users/queries/detail'
import { useNonDeletedMessages } from './list'
import { addMentionDataToCache } from './shared/utils'

type MentionVariables = {
  conversationId: string
  messageId: string
  userIds: string[]
}

export function useCreateMentions() {
  const client = useQueryClient()
  const { server } = useServer()
  const { sendWebsocketMessage } = useChatWebsocketContext()
  return useMutation({
    mutationFn: createMentions,
    onSuccess: async event => {
      await addMentionDataToCache(client, event)
      sendWebsocketMessage({
        action: 'sendMessageEvent',
        messageEvent: event,
      })
    },
  })

  function createMentions({
    conversationId,
    messageId,
    userIds,
  }: MentionVariables) {
    return server.post<Response.CreateMentionsEvent>(
      `/v2/chat/conversations/${conversationId}/messages/${messageId}/mention`,
      { users: userIds }
    )
  }
}

export function useMentionsUnreadCount() {
  const { data: user } = useUser()
  const { data: conversations } = useConversations()

  const { data: mentionMessages } = useNonDeletedMessages(data =>
    data.messages.filter(message =>
      message.mentions?.find(mention => mention.user.id === user?.id)
    )
  )

  const newMentionMessagesCount = mentionMessages?.filter(
    message =>
      Number(
        message.mentions?.find(mention => mention.user.id === user?.id)
          ?.timestamp
      ) >=
      (conversations?.find(
        conversation => conversation.id === message.conversationId
      )?.status?.lastViewedTimestamp ?? 0)
  ).length

  return newMentionMessagesCount ?? 0
}
